import { driver } from 'driver.js';

import 'driver.js/dist/driver.css';

const commonOptions = {
    showProgress: true,

    showButtons: ['next', 'previous', 'close'],

    popoverClass: 'cz2__driver-theme',

    onDestroyed: () => {
        // Driver wants to move things after destroying the instance, reset them back.
        setTimeout(() => {
            document.querySelector('.cz2').scrollTop = 0;
        }, 0);
    },
};

function shouldDoTour(type, deviceTarget) {
    if (deviceTarget === 'large') {
        if (window.innerWidth < 1024) {
            return false;
        }
    }

    if (deviceTarget === 'small') {
        if (window.innerWidth >= 1024) {
            return false;
        }
    }

    const key = `tour-${type}`;

    if (sessionStorage.getItem(key) === 'shown') {
        return false;
    }

    sessionStorage.setItem(key, 'shown');

    return true;
}

export function intro() {
    if (!shouldDoTour('intro', 'large')) {
        return null;
    }

    const driverConfiguration = driver({
        ...commonOptions,

        steps: [
            {
                element: '.cz__tour__colors',
                popover: {
                    title: 'Color',
                    description: 'Select a color and other color customization options, if available.',
                    side: 'left',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__images',
                popover: {
                    title: 'Add images',
                    description: 'Add one or more images from the gallery or upload your own.',
                    side: 'left',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__text',
                popover: {
                    title: 'Add text lines',
                    description: 'Add one or more text lines. Select a font, text size, or alignment options',
                    side: 'left',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__monogram',
                popover: {
                    title: 'Add monogramming',
                    description: 'Choose a monogramming style and enter up to 3 characters for the initials.',
                    side: 'left',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__group',
                popover: {
                    title: 'Group personalization',
                    // eslint-disable-next-line max-len
                    description: 'Group personalization is intended for orders that will have different text on each bottle.',
                    side: 'left',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__view',
                popover: {
                    title: 'Preview your item',
                    // eslint-disable-next-line max-len
                    description: 'Swipe left or right on the image to preview the front or back view of the item, or tap on the “Front” or “Back” button.',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '.cz__tour__done',
                popover: {
                    title: 'Review & Buy',
                    // eslint-disable-next-line max-len
                    description: 'Click here after you have selected the personalization options such as text, image, or monogramming. You can review the configurations, change the quantity to buy, and purchase your personalized items.',
                    side: 'left',
                    align: 'start',
                },
            },
        ],
    });

    driverConfiguration.drive();

    return driverConfiguration;
}

export function introSmall() {
    if (!shouldDoTour('intro', 'small')) {
        return null;
    }

    const driverConfiguration = driver({
        ...commonOptions,

        steps: [
            {
                element: '.cz__tour__small-colors',
                popover: {
                    title: 'Color',
                    description: 'Select a color and other color customization options, if available.',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '.cz__tour__small-images',
                popover: {
                    title: 'Add images',
                    description: 'Add one or more images from the gallery or upload your own.',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '.cz__tour__small-text',
                popover: {
                    title: 'Add text lines',
                    description: 'Add one or more text lines. Select a font, text size, or alignment options',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '.cz__tour__view',
                popover: {
                    title: 'Preview your item',
                    // eslint-disable-next-line max-len
                    description: 'Swipe left or right on the image to preview the front or back view of the item, or tap on the “Front” or “Back” button.',
                    side: 'bottom',
                    align: 'center',
                },
            },
            {
                element: '.cz__tour__small-done',
                popover: {
                    title: 'Review & Buy',
                    // eslint-disable-next-line max-len
                    description: 'Click here after you have selected the personalization options such as text, image, or monogramming. You can review the configurations, change the quantity to buy, and purchase your personalized items.',
                    side: 'top',
                    align: 'center',
                },
            },
        ],
    });

    driverConfiguration.drive();

    return driverConfiguration;
}

export function edit() {
    if (!shouldDoTour('edit', 'large')) {
        return null;
    }

    const driverConfiguration = driver({
        ...commonOptions,

        steps: [
            {
                element: '.cz__tour__handles.cz__tour__visible',
                popover: {
                    title: 'Adjust size and position',
                    // eslint-disable-next-line max-len
                    description: 'Click and drag the corners of the text box, image, or monogram to adjust the size.  Click and drag the center of the text box, image, or monogram to change the position.',
                    side: 'right',
                    align: 'start',
                },
                onNextClick: async () => {
                    await this.$nextTick();
                    driverConfiguration.moveNext();
                },
            },
            {
                element: '.cz__tour__rotate.cz__tour__visible',
                popover: {
                    title: 'Rotate',
                    // eslint-disable-next-line max-len
                    description: 'Click and drag the wheel to rotate the text or image by 0, 90, 180 or 360-degrees.',
                    side: 'right',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__view',
                popover: {
                    title: 'Change the design area',
                    // eslint-disable-next-line max-len
                    description: 'Move your text, image, or monogram to another customization area (front, back top, or back).  Applying a customization to each area will incur an upcharge. See your cart for final prices.',
                    side: 'right',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__edit-options',
                popover: {
                    title: 'Design options',
                    // eslint-disable-next-line max-len
                    description: 'Choose an alignment option for your text, monogram, or image, change their layering order, or remove them.',
                    side: 'left',
                    align: 'start',
                },
            },
        ],
    });

    driverConfiguration.drive();

    return driverConfiguration;
}

export function editSmall() {
    if (!shouldDoTour('edit', 'small')) {
        return null;
    }

    const driverConfiguration = driver({
        ...commonOptions,

        steps: [
            {
                element: '.cz__tour__handles.cz__tour__visible',
                popover: {
                    title: 'Adjust size and position',
                    // eslint-disable-next-line max-len
                    description: 'Click and drag the corners of the text box, image, or monogram to adjust the size.  Click and drag the center of the text box, image, or monogram to change the position.',
                    side: 'bottom',
                    align: 'center',
                },
                onNextClick: async () => {
                    await this.$nextTick();
                    driverConfiguration.moveNext();
                },
            },
            {
                element: '.cz__tour__rotate.cz__tour__visible',
                popover: {
                    title: 'Rotate',
                    // eslint-disable-next-line max-len
                    description: 'After text is added, click and drag the wheel to rotate the text or image by 0, 90, 180 or 360-degrees.',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '.cz__tour__view',
                popover: {
                    title: 'Change the design area',
                    // eslint-disable-next-line max-len
                    description: 'Move your text, image, or monogram to another customization area (front, back top, or back).  Applying a customization to each area will incur an upcharge. See your cart for final prices.',
                    side: 'top',
                    align: 'center',
                },
            },
            {
                element: '.cz__tour__edit-options',
                popover: {
                    title: 'Design options',
                    // eslint-disable-next-line max-len
                    description: 'Choose an alignment option for your text, monogram, or image, change their layering order, or remove them.',
                    side: 'top',
                    align: 'center',
                },
            },
        ],
    });

    driverConfiguration.drive();

    return driverConfiguration;
}

export function list() {
    if (!shouldDoTour('list', 'large')) {
        return null;
    }

    const driverConfiguration = driver({
        ...commonOptions,

        steps: [
            {
                element: '.cz__tour__list-add',
                popover: {
                    title: 'Add more layers',
                    // eslint-disable-next-line max-len
                    description: 'You can add more than one text, image or monogram to each print area.  Use the layering options to bring designs up front or send them to the back.',
                    side: 'left',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__list-edit',
                popover: {
                    title: 'Edit & remove',
                    // eslint-disable-next-line max-len
                    description: 'Click the edit button to adjust a text or image layer position or rotation.  Click the remove button to delete a layer.',
                    side: 'left',
                    align: 'start',
                },
            },
            {
                element: '.cz__tour__list-back',
                popover: {
                    title: 'Return to the main menu',
                    description: 'Click “Back” to return to the main customization menu.',
                    side: 'left',
                    align: 'start',
                },
            },
        ],
    });

    driverConfiguration.drive();

    return driverConfiguration;
}
